import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import sessionStorage from 'redux-persist/lib/storage/session';
import { StateSliceName } from 'state/StateSliceName';
import { RouteDefinitions } from 'routes';

interface CurrentClinicState {
  isImpersonating: boolean;
  clinicId: string | undefined;
  /**
   * Note: The `clinicId` stored in this slice is ONLY used to tell Apollo Client which clinic ID to include in headers.
   * To get the user's current clinic ID throughout the application, consume the useClinicUser hook.
   */
  wallpaperMode: boolean;
  defaultRedirect: typeof RouteDefinitions[keyof typeof RouteDefinitions] | null;
  impersonatingUser?: {
    email: string;
    firstName: string;
    lastName: string;
    isAdmin: boolean;
  };
}

const initialState: CurrentClinicState = {
  isImpersonating: false,
  clinicId: undefined,
  wallpaperMode: false,
  defaultRedirect: null,
  impersonatingUser: undefined,
};

export const currentClinicSlice = createSlice({
  name: StateSliceName.CurrentClinic,
  initialState,
  reducers: {
    resetCurrentClinicState: () => initialState,
    updateCurrentClinicState: (state, action: PayloadAction<Partial<CurrentClinicState>>) => {
      return { ...state, ...action.payload };
    },
    toggleWallpaperMode: (state) => {
      state.wallpaperMode = !state.wallpaperMode;
    },
    setDefaultRedirect: (
      state,
      action: PayloadAction<typeof RouteDefinitions[keyof typeof RouteDefinitions] | null>,
    ) => {
      state.defaultRedirect = action.payload;
    },
  },
});

export const { resetCurrentClinicState, updateCurrentClinicState, toggleWallpaperMode, setDefaultRedirect } =
  currentClinicSlice.actions;

const persistConfig = {
  key: 'currentClinic',
  version: 1,
  storage: sessionStorage,
};

export default persistReducer(persistConfig, currentClinicSlice.reducer);
